import appLogger from '@/utils/logger';
import { ERROR_CODE, PATH, STORAGE_KEY } from '@/constants';
import { WORKSPACE_CHANGED_MESSAGE, WORKSPACE_STORAGE_KEY } from '@/constants/workspace';
import { supabase } from '@/services/supabase';
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import { getPublicApiBaseUrl } from '@/utils/publicApiBaseUrl';
import { clearAuthLocalStorage } from '@/utils/durableLocalStorage';
import { isNonInteractiveAuth, resolveRequestAuth } from '@/utils/requestAuth';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Pin a request to the workspace whose data the caller is showing. The workspace header is
     * re-read from storage on every send, and another tab can switch it in between — including
     * between a request and its token-refresh retry. A pinned request that finds another
     * workspace active is refused before it is sent.
     */
    expectedWorkspaceId?: number | string;
  }
}

/**
 * Response interceptor used to reject `error.response` only, so many callers expected either:
 * - `{ data, status, headers }` (unwrapped), or
 * - `err.response.data` (standard AxiosError) — but the latter was broken (GH#1551).
 *
 * We reject the full AxiosError and copy `response.data` to `data` so both patterns work.
 */
type AxiosErrorWithBody = AxiosError & { data?: unknown };

function rejectNormalizedAxiosError(error: AxiosError): Promise<never> {
  if (error.response && error.response.data !== undefined) {
    (error as AxiosErrorWithBody).data = error.response.data;
  }
  return Promise.reject(error);
}

// Workspace sync headers processing for frontend/backend synchronization
const processWorkspaceSyncHeaders = (response: AxiosResponse) => {
  try {
    const headers = response.headers;

    // Extract workspace sync headers
    const syncHeaders = {
      currentId: headers['x-workspace-current-id'],
      syncStatus: headers['x-workspace-sync-status'],
      context: headers['x-workspace-context'],
      expectedId: headers['x-workspace-expected-id'],
      clientId: headers['x-workspace-client-id'],
      action: headers['x-workspace-action'],
      mismatchType: headers['x-workspace-mismatch-type'],
      timestamp: headers['x-workspace-sync-timestamp'],
    };

    // Only process if we have sync headers
    if (!syncHeaders.currentId || !syncHeaders.syncStatus) {
      return;
    }

    // An embed session's workspace is bound server-side to the token; the app's
    // localStorage workspace belongs to whatever else is open in this browser
    // and must not be rewritten from inside a Salesforce iframe.
    if (typeof window !== 'undefined' && resolveRequestAuth().mode === 'embed') {
      return;
    }

    const currentLocalWorkspace = typeof window !== 'undefined' ? localStorage.getItem(WORKSPACE_STORAGE_KEY) : null;

    // CRITICAL FIX: Ignore workspace sync headers if workspace was recently switched
    // This prevents race condition where:
    // 1. User switches workspace and localStorage is updated
    // 2. Page reloads and fires multiple API calls
    // 3. Backend might still return old workspace ID briefly
    // 4. Axios interceptor would overwrite localStorage back to old ID
    const lastSwitchTimestamp = typeof window !== 'undefined' ? localStorage.getItem('workspaceSwitchTimestamp') : null;
    if (lastSwitchTimestamp) {
      const timeSinceSwitch = Date.now() - parseInt(lastSwitchTimestamp, 10);
      // Ignore sync headers for 30 seconds after workspace switch
      // This prevents race conditions where in-flight requests return stale workspace IDs
      if (timeSinceSwitch < 30000) {
        appLogger.info('Ignoring workspace sync headers - recent switch detected', {
          timeSinceSwitch,
          local: currentLocalWorkspace,
          server: syncHeaders.currentId,
        });
        return;
      }
    }

    // Log sync status for debugging
    appLogger.info('🔄 Workspace sync headers received:', {
            local: currentLocalWorkspace,
            server: syncHeaders.currentId,
            status: syncHeaders.syncStatus,
            action: syncHeaders.action,
            context: syncHeaders.context,
          });

    // Handle workspace state mismatches
    if (syncHeaders.syncStatus === 'mismatch') {
      handleWorkspaceSyncMismatch(syncHeaders, currentLocalWorkspace);
    } else if (syncHeaders.syncStatus === 'synced') {
      // Ensure local storage matches server state
      if (currentLocalWorkspace !== syncHeaders.currentId) {
        appLogger.info('🔄 Syncing workspace ID to match server:', { data: syncHeaders.currentId });
        if (typeof window !== 'undefined') {
          localStorage.setItem(WORKSPACE_STORAGE_KEY, syncHeaders.currentId);
        }

        // Dispatch event for workspace context updates
        window.dispatchEvent(
          new CustomEvent('workspace-sync-corrected', {
            detail: {
              previousId: currentLocalWorkspace,
              correctedId: syncHeaders.currentId,
              context: syncHeaders.context,
              source: 'server_response',
            },
          }),
        );
      }
    }
  } catch (error) {
    appLogger.warn('Error processing workspace sync headers:', { data: error });
  }
};

// Handle workspace sync mismatch scenarios
const handleWorkspaceSyncMismatch = (syncHeaders: any, localWorkspaceId: string | null) => {
  appLogger.info('🔄 Workspace sync mismatch detected:', {
        local: localWorkspaceId,
        server: syncHeaders.currentId,
        action: syncHeaders.action,
        mismatchType: syncHeaders.mismatchType,
      });

  switch (syncHeaders.action) {
    case 'sync_to_server':
      // Server is authoritative, update frontend to match
      if (syncHeaders.currentId !== localWorkspaceId) {
        if (typeof window !== 'undefined') {
          localStorage.setItem(WORKSPACE_STORAGE_KEY, syncHeaders.currentId);
        }

        // Dispatch sync event for workspace provider to handle
        window.dispatchEvent(
          new CustomEvent('workspace-sync-mismatch', {
            detail: {
              type: 'sync_to_server',
              previousId: localWorkspaceId,
              correctedId: syncHeaders.currentId,
              context: syncHeaders.context,
              mismatchType: syncHeaders.mismatchType,
              source: 'backend_correction',
            },
          }),
        );
      }
      break;

    case 'validate':
      // Both frontend and backend need validation
      window.dispatchEvent(
        new CustomEvent('workspace-sync-mismatch', {
          detail: {
            type: 'validation_needed',
            localId: localWorkspaceId,
            serverId: syncHeaders.currentId,
            expectedId: syncHeaders.expectedId,
            context: syncHeaders.context,
            mismatchType: syncHeaders.mismatchType,
            source: 'validation_required',
          },
        }),
      );
      break;

    default:
      appLogger.warn('Unknown workspace sync action:', { data: syncHeaders.action });
  }
};

// Global AbortController for workspace switches
// When switching workspace, we abort all in-flight requests to prevent race conditions
let globalAbortController: AbortController | null = null;

// Cancel all in-flight requests (called when switching workspace)
export const cancelAllRequests = () => {
  if (globalAbortController) {
    appLogger.info('🚫 Cancelling all in-flight requests due to workspace switch');
    globalAbortController.abort();
    globalAbortController = null;
  }
  // Create new controller for future requests
  globalAbortController = new AbortController();
};

// Initialize on module load
globalAbortController = new AbortController();

const createApi = (): AxiosInstance => {
  const instance = axios.create({
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add interceptors and other configuration here

  const setAuthorizationHeader = (request: AxiosRequestConfig | any, token: string) => {
    request.headers.Authorization = `Bearer ${token}`;
  };

  const onRequest = (config: AxiosRequestConfig | any) => {
    config.baseURL = getPublicApiBaseUrl();

    // CRITICAL FIX: Add global abort signal to all requests
    // This allows cancelling all in-flight requests when switching workspace
    if (globalAbortController && !config.signal) {
      config.signal = globalAbortController.signal;
    }

    // CRITICAL FIX: Only access browser storage in browser environment.
    // Credential precedence (embed session > Supabase > CTI) lives in resolveRequestAuth.
    if (typeof window !== 'undefined') {
      const { token, workspaceId } = resolveRequestAuth();
      if (token) {
        setAuthorizationHeader(config, token);
      }

      // Add workspace header for private API calls
      if (workspaceId && config.url?.includes('/private/')) {
        config.headers['x-workspace-id'] = workspaceId;
      }

      // Runs on every dispatch, so a token-refresh retry is checked again too.
      if (config.expectedWorkspaceId != null && String(config.expectedWorkspaceId) !== String(workspaceId ?? '')) {
        throw new Error(WORKSPACE_CHANGED_MESSAGE);
      }
    }

    return config;
  };

  const onRequestError = (error: AxiosError) => {
    return Promise.reject(error);
  };

  const onResponse = (response: AxiosResponse) => {
    // Process workspace sync headers from backend
    processWorkspaceSyncHeaders(response);
    return response;
  };

  const handleRetrieveNewSession = async (error: AxiosError) => {
    const originalRequest = error.config as any;
    
    // Prevent infinite retry loops
    if (originalRequest._retry) {
      if (typeof window !== 'undefined') {
        appLogger.info('Token refresh failed, redirecting to login...');
        clearAuthLocalStorage();
        window.location.pathname = PATH.LOGIN;
      }
      return rejectNormalizedAxiosError(error);
    }
    
    originalRequest._retry = true;
    
    try {
      // Embed / CTI API-token mode: do not attempt Supabase refresh (there is no Supabase session).
      if (typeof window !== 'undefined' && isNonInteractiveAuth(resolveRequestAuth().mode)) {
        return rejectNormalizedAxiosError(error);
      }

      const { data, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        appLogger.error('Failed to refresh session:', { data: refreshError });
        if (typeof window !== 'undefined') {
          clearAuthLocalStorage();
          window.location.pathname = PATH.LOGIN;
        }
        return rejectNormalizedAxiosError(error);
      }
      
      const { session } = data;
      const { access_token } = session || {};

      if (access_token && typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY.ACCESS_TOKEN, access_token);
        // Update the authorization header for the retry request
        originalRequest.headers.Authorization = 'Bearer ' + access_token;
        // Also update default headers for future requests
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
        return instance(originalRequest);
      } else if (typeof window !== 'undefined') {
        appLogger.info('No access token received, redirecting to login...');
        clearAuthLocalStorage();
        window.location.pathname = PATH.LOGIN;
        return rejectNormalizedAxiosError(error);
      }
    } catch (refreshError) {
      appLogger.error('Error during session refresh:', { data: refreshError });
      if (typeof window !== 'undefined') {
        clearAuthLocalStorage();
        window.location.pathname = PATH.LOGIN;
      }
      return rejectNormalizedAxiosError(error);
    }
  };

  const onResponseError = async (error: AxiosError | any) => {
    // Silently ignore aborted requests (intentional cancellation during workspace switch)
    if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
      appLogger.info('Request cancelled (workspace switch)', { 
        url: error.config?.url,
        method: error.config?.method 
      });
      return Promise.reject(error);
    }

    // Track API errors in Datadog RUM
    if (typeof window !== 'undefined' && error.isAxiosError) {
      try {
        datadogRum.addError(error, {
          source: 'network',
          http_status: error.response?.status || 0,
          http_method: error.config?.method?.toUpperCase(),
          http_url: error.config?.url,
          error_code: error.code,
          error_type: error.response?.data?.error || 'api_error',
          workspace_id: localStorage.getItem(WORKSPACE_STORAGE_KEY) || 'unknown',
        });
      } catch (ddError) {
        // Silently fail if Datadog RUM is not initialized
        appLogger.warn('Failed to track error in Datadog:', { data: ddError });
      }
    }

    if (error.isAxiosError) {
      // Network errors have no response object — reject with the original error
      // so consumers get a proper Error instance (not undefined)
      if (error.code === ERROR_CODE.ERR_NETWORK) {
        appLogger.error('Network error — server unreachable or request blocked', {
          data: { url: error.config?.url, method: error.config?.method },
        });
        return Promise.reject(error);
      }

      switch (error.response?.status) {
        case ERROR_CODE.UNAUTHORIZED: {
          // Embed / CTI API-token mode: do not trigger Supabase refresh or redirect-to-login.
          if (typeof window !== 'undefined' && isNonInteractiveAuth(resolveRequestAuth().mode)) {
            return rejectNormalizedAxiosError(error);
          }

          // Check if this is an integration configuration error (not a MarketBetter auth issue)
          const errorData = error.response?.data;
          const errorMessage = errorData?.error || errorData?.message || '';
          const errorDetails = errorData?.details || {};

          // Integration configuration errors should NOT trigger auth refresh.
          // Only match errors that clearly indicate a THIRD-PARTY credential issue
          // (API key invalid, token expired on external service), not MarketBetter's
          // own auth expiry on /integration/* management endpoints (GH#603 / vector-settings fix).
          const isIntegrationConfigError =
            errorDetails?.action === 'update_api_key' ||
            errorDetails?.action === 'configure_api_key' ||
            (errorData as any)?.requiresReconnect === true ||
            (errorData as any)?.code === 'HUBSPOT_TOKEN_EXPIRED' ||
            (errorData as any)?.code === 'HUBSPOT_AUTH_FAILED' ||
            errorMessage === 'MicrosoftAuthError' ||
            errorMessage.includes('API key') ||
            errorMessage.includes('API token') ||
            /\b(reconnect|re-authenticate)\b/i.test(errorMessage);

          if (isIntegrationConfigError) {
            appLogger.warn('Integration configuration error (401):', { data: errorMessage });
            return rejectNormalizedAxiosError(error);
          }

          // Genuine auth error — attempt session refresh
          return await handleRetrieveNewSession(error);
        }

        case ERROR_CODE.FORBIDDEN: {
          // Show user-friendly messages for known subscription codes
          const forbiddenCode = (error.response?.data as any)?.code;
          if (typeof window !== 'undefined') {
            if (forbiddenCode === 'NO_SUBSCRIPTION') {
              // Suppress during onboarding (subscribing) and immediately after trial activation
              // (webhook may not have fired yet when Stripe redirects back)
              const isOnboarding = window.location.pathname.startsWith('/onboarding');
              const isPostCheckoutReturn =
                window.location.search.includes('trial_success=1')
                || window.location.search.includes('subscription_success=1');
              const isAlreadyOnBilling = window.location.search.includes('tab=billing-and-usage');
              const isOnSettings = window.location.pathname.startsWith('/settings');
              if (!isOnboarding && !isPostCheckoutReturn && isOnSettings && !isAlreadyOnBilling) {
                // Only show toast + redirect if NOT already being handled by NoSubscriptionScreen.
                // ProtectedLayout renders NoSubscriptionScreen for non-settings pages, so the
                // interceptor only needs to handle the edge case of direct API calls from settings.
                if (!(window as any).__noSubRedirectPending) {
                  (window as any).__noSubRedirectPending = true;
                  toast.error(
                    'Your trial has ended. Subscribe to continue using MarketBetter.',
                    {
                      toastId: 'no-subscription',
                      onClick: () => { window.location.href = '/settings?tab=billing-and-usage'; },
                      style: { cursor: 'pointer' },
                    },
                  );
                  setTimeout(() => {
                    if (
                      window.location.search.includes('trial_success=1')
                      || window.location.search.includes('subscription_success=1')
                    ) {
                      (window as any).__noSubRedirectPending = false;
                      return;
                    }
                    window.location.href = '/settings?tab=billing-and-usage';
                  }, 3000);
                }
              }
            }
          }
          return rejectNormalizedAxiosError(error);
        }

        case ERROR_CODE.UNPROCESSABLE_ENTITY:
          return rejectNormalizedAxiosError(error);

        case ERROR_CODE.TOO_MANY_REQUESTS:
          appLogger.warn('Rate limited (429)', {
            data: { url: error.config?.url, retryAfter: error.response?.headers?.['retry-after'] },
          });
          return rejectNormalizedAxiosError(error);

        case ERROR_CODE.INTERNAL_SERVER_ERROR:
          return rejectNormalizedAxiosError(error);

        case ERROR_CODE.NOT_FOUND:
          return rejectNormalizedAxiosError(error);

        default:
          return rejectNormalizedAxiosError(error);
      }
    }

    // Non-axios errors — reject with the original error for a meaningful stack trace
    return Promise.reject(error);
  };

  instance.interceptors.request.use(onRequest, onRequestError);
  instance.interceptors.response.use(onResponse, onResponseError);

  return instance;
};

export const apiInstance = createApi();
